import { useEffect, useState, useCallback, Fragment, useMemo } from 'react';
import { useRouter } from '../util/router';
import { getPreviewData, getShowById } from '../util/util';

function OVR() {
  const router = useRouter();
  const { id } = router.query;
  const [previews, setPreviews] = useState(null);
  const [show, setShow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchShowData = useCallback(async (showId, abortController) => {
    try {
      const show = await getShowById(showId);
      if (!abortController.signal.aborted) {
        setShow(show[0]);
      }
    } catch (error) {
      if (!abortController.signal.aborted) {
        console.error('Error fetching show data:', error);
        setError('Failed to load show data');
      }
    }
  }, []);

  const fetchPreviewData = useCallback(async (showId, abortController) => {
    try {
      const previews = await getPreviewData(showId);
      if (!abortController.signal.aborted) {
        setPreviews(previews);
      }
    } catch (error) {
      if (!abortController.signal.aborted) {
        console.error('Error fetching preview data:', error);
        setError('Failed to load preview data');
      }
    }
  }, []);

  useEffect(() => {
    if (!id) return;

    const abortController = new AbortController();
    setLoading(true);
    setError(null);

    Promise.all([
      fetchPreviewData(id, abortController),
      fetchShowData(id, abortController),
    ]).finally(() => {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    });

    return () => {
      abortController.abort();
    };
  }, [id, fetchPreviewData, fetchShowData]);

  const memoizedPreviews = useMemo(() => {
    if (!previews) return null;

    return previews.map((preview, index) => (
      <div key={index} className="text-center flex flex-col justify-end h-full">
        {preview.image_url && (
          <div className="mb-6">
            <img
              src={preview.image_url}
              alt={preview.row1 || 'Preview image'}
              className="w-full h-auto object-contain"
              loading="lazy"
              decoding="async"
              width="300"
              height="400"
            />
          </div>
        )}
        {preview.row1 && <p className="text-xs text-gray-600">{preview.row1}</p>}
        {preview.row2 && <p className="text-xs text-gray-600">{preview.row2}</p>}
        {preview.row3 && <p className="text-xs text-gray-500">{preview.row3}</p>}
        {preview.row4 && <p className="text-xs text-gray-500">{preview.row4}</p>}
        {preview.row5 && <p className="text-xs text-gray-500">{preview.row5}</p>}
        {preview.row6 && <p className="text-xs text-gray-500">{preview.row6}</p>}
        {preview.row7 && <p className="text-xs text-gray-500">{preview.row7}</p>}
        {preview.row8 && <p className="text-xs text-gray-500">{preview.row8}</p>}
      </div>
    ));
  }, [previews]);

  const showHeader = useMemo(() => {
    if (!show) return null;

    return (
      <div>
        <h1 className="text-2xl font-light text-gray-900 mb-2">{show.display_title}</h1>
        <h2 className="text-lg text-gray-500">{show.venues.venue_name}</h2>
      </div>
    );
  }, [show]);

  if (error) {
    return (
      <div className="min-h-screen bg-white font-oswald flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="min-h-screen bg-white font-oswald">
        <div className="max-w-6xl mx-auto px-12 py-20">
          <div className="text-center mb-20">
            {loading && !show ? (
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-2"></div>
                <div className="h-6 bg-gray-200 rounded w-48 mx-auto"></div>
              </div>
            ) : (
              showHeader
            )}
          </div>

          {loading && !previews ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-16">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="bg-gray-200 h-64 mb-4 rounded"></div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : previews && previews.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-16">
              {memoizedPreviews}
            </div>
          ) : (
            <div className="text-center">
              <p className="text-gray-500">No preview data available</p>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
}

export default OVR;
